import React from "react";
import { graphql, useFragment } from "react-relay";
import { useHistory } from "react-router";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowAltCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { capitalize } from "lodash";
import moment, { type Moment } from "moment";
import { Popup, Visibility } from "semantic-ui-react";
import uuidv4 from "uuid/v4";

import { chatWithSales, ProductFamilies } from "~/billing/utils";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isNative, isTeams } from "~/lib/utils";
import { Button } from "~/ui";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";

import type { NoteAndFeedbackHistoryPaywall_viewer$key as HistoryViewerKey } from "~/__generated__/NoteAndFeedbackHistoryPaywall_viewer.graphql";

import noteCardStyles from "~/stream/css/NoteCard.module.css";
import noteHeaderStyles from "~/stream/NoteHeader/NoteHeader.module.css";

import styles from "./css/Billing.module.css";

const paywalledNoteClassname = cx(styles.paywalledNote, noteHeaderStyles.paywalledNote, noteCardStyles.paywalledNote);

interface PaywallProps {
	viewer: HistoryViewerKey;
	children: React.ReactElement;
	feature: "note" | "feedback";
	text: string;
	creationDate: Moment;
	contentLifted?: boolean;
	disabled?: boolean;
}

export default function NoteAndFeedbackHistoryPaywall({
	viewer,
	feature,
	text,
	creationDate,
	children,
	contentLifted = false,
	disabled = false,
}: PaywallProps) {
	const analyticsProperties = React.useMemo(
		() => ({
			fingerprint: uuidv4(),
			ux: "dimmer",
			feature: "history",
		}),
		[],
	);

	const history = useHistory();

	const { user, account } = useFragment(
		graphql`
			fragment NoteAndFeedbackHistoryPaywall_viewer on Viewer {
				user {
					isGuest
				}
				account {
					billingAccount {
						restrictedMetrics
						hasAccessToHistory: hasAccessToFeature(feature: history)
						historyLimit: usageLimitForFeature(feature: history)
					}
				}
			}
		`,
		viewer,
	);

	const { hasAccessToHistory, historyLimit, restrictedMetrics } = account.billingAccount;

	const expiryDate = React.useMemo(() => {
		const { historyLimit } = account.billingAccount;
		if (!historyLimit) return moment();

		return moment().subtract(historyLimit, "days");
	}, [account.billingAccount]);

	const accessDenied = React.useMemo(() => {
		if (disabled) return false;
		return moment(creationDate).isBefore(expiryDate);
	}, [disabled, creationDate, expiryDate]);

	const onPaywallVisible = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Encountered", analyticsProperties);
	}, [analyticsProperties]);

	const onLearnClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_marketing" });
	}, [analyticsProperties]);

	const onChatWithSales = React.useCallback(() => {
		chatWithSales(`Hi! I'm interested in upgrading to unlock ${capitalize(feature)} History.`);
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "chat_with_sales" });
	}, [analyticsProperties, feature]);

	const onUpdateNowClick = React.useCallback(() => {
		if (isTeams) {
			window.open(`${window.location.origin}/account/billing/plan/?from=upgrade`);
		} else {
			history.push("/account/billing/plan/?from=upgrade");
		}

		const { feedbackCount, noteCount } = restrictedMetrics;

		dispatchAnalyticsTrack("Paywall Action Taken", {
			...analyticsProperties,
			action: "redirect_to_billing",
			restrictedFeedback: feedbackCount,
			restrictedNotes: noteCount,
		});
	}, [restrictedMetrics, analyticsProperties, history]);

	let tooltipText = "This content has not\nbeen deleted.";
	if (!isNative) tooltipText += " Upgrade\nto receive unlimited\nhistory.";

	let isPaywall = !hasAccessToHistory && accessDenied;
	if (!isPaywall) return children;

	const visibilityProps = {
		as: "div",
		once: true,
		continuous: false,
		updateOn: "repaint" as const,
		className: cx(styles.paywallContent, contentLifted ? styles.lifted : ""),
		onOnScreen: onPaywallVisible,
	};

	if (user.isGuest)
		return (
			<div className={styles.paywallWrapper}>
				<div className={styles.paywallContainer}>
					<Visibility {...visibilityProps}>
						<span className={styles.paywallText}>
							Viewing {text} over {historyLimit} days ago is not available for this workspace.
						</span>
					</Visibility>
				</div>
				<div className={paywalledNoteClassname}>{children}</div>
			</div>
		);

	return (
		<div className={styles.paywallWrapper}>
			<div className={styles.paywallContainer}>
				<Visibility {...visibilityProps}>
					<div>
						<FontAwesomeIcon className={styles.paywallIcon} icon={faArrowAltCircleUp} />
					</div>
					<div>
						<div className={styles.paywallHeading}>
							<strong className="text-underlay text-underlay-orange">{capitalize(feature)} History</strong> is a Pro
							plan feature
						</div>
						<span className={styles.paywallText}>
							Go back in time and view {text} over {historyLimit} days ago.{" "}
							<HelpCenterLink articleId={ProductFamilies["PRO"].helpArticleId} onClick={onLearnClick}>
								Learn more
							</HelpCenterLink>
							<br />
							<strong>Upgrade to our Team plan and get unlimited access to your entire history and more.</strong>
						</span>
						&nbsp;
						<span>
							<Popup
								inverted
								size="tiny"
								content={tooltipText}
								position="bottom center"
								trigger={<FontAwesomeIcon icon={faInfoCircle} />}
							/>
						</span>
					</div>
					{!isNative && (
						<div className={styles.paywallControls}>
							<Button size="wide" variant="secondary" className={styles.paywallUpgradeButton} onClick={onChatWithSales}>
								Chat with sales
							</Button>
							<Button size="wide" variant="billing" className={styles.paywallUpgradeButton} onClick={onUpdateNowClick}>
								Upgrade
							</Button>
						</div>
					)}
				</Visibility>
			</div>
			<div className={paywalledNoteClassname}>{children}</div>
		</div>
	);
}
